// src/services/reportService.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Papa from "papaparse";

const IMA_ORANGE = [224, 92, 46];
const IMA_DARK = [13, 15, 18];

function timestamp() {
  return new Date().toLocaleString("en-US", { hour12: false });
}

function filename(module) {
  const d = new Date();
  const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}_${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
  return `IMA_${module}_${stamp}`;
}

// ─── PDF ──────────────────────────────────────────────────────────────────────

function addHeader(doc, module, operator, ticketRef) {
  doc.setFillColor(...IMA_DARK);
  doc.rect(0, 0, 210, 28, "F");

  doc.setTextColor(224, 92, 46);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("IMA Response Suite", 14, 12);

  doc.setTextColor(180, 180, 190);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Module: ${module}`, 14, 20);
  doc.text(`Generated: ${timestamp()}`, 100, 20);

  doc.setTextColor(...IMA_DARK);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  let y = 36;
  doc.text(`Operator: ${operator}`, 14, y);
  if (ticketRef) doc.text(`Ticket ref: ${ticketRef}`, 100, y);
  doc.setDrawColor(230, 230, 230);
  doc.line(14, y + 4, 196, y + 4);
  return y + 12;
}

export function exportPurgePDF({ results, criteria, operator, ticketRef }) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Phishing Purge", operator, ticketRef);

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...IMA_DARK);
  doc.text("Search Criteria", 14, y);
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  Object.entries(criteria).filter(([, v]) => v).forEach(([k, v]) => {
    doc.text(`${k}: ${v}`, 18, y);
    y += 5;
  });
  y += 4;

  const deleted = results.filter((r) => r.status === "deleted");
  const failed = results.filter((r) => r.status === "failed");

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text(`Results: ${results.length} matches · ${deleted.length} deleted · ${failed.length} failed`, 14, y);
  y += 6;

  autoTable(doc, {
    startY: y,
    head: [["Mailbox", "Subject", "Sender", "Received", "Result"]],
    body: results.map((r) => [
      r.userEmail,
      r.subject?.substring(0, 40),
      r.sender,
      new Date(r.received).toLocaleString(),
      r.status === "deleted" ? "DELETED" : "FAILED",
    ]),
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: IMA_ORANGE, textColor: [255, 255, 255], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 248, 250] },
    columnStyles: { 4: { fontStyle: "bold" } },
    didDrawCell: (data) => {
      if (data.section === "body" && data.column.index === 4) {
        const val = data.cell.raw;
        doc.setTextColor(val === "DELETED" ? 5 : 220, val === "DELETED" ? 150 : 30, val === "DELETED" ? 105 : 30);
      }
    },
  });

  doc.save(`${filename("Purge")}.pdf`);
}

export function exportDelegationPDF({ delegation, operator, ticketRef }) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Mailbox Delegation", operator, ticketRef);

  autoTable(doc, {
    startY: y,
    head: [["Field", "Value"]],
    body: [
      ["Target mailbox", delegation.targetEmail],
      ["Access level", delegation.level],
      ["Granted by", operator],
      ["Duration", `${delegation.hours} hours`],
      ["Granted at", new Date(delegation.granted).toLocaleString()],
      ["Expires at", new Date(delegation.expiry).toLocaleString()],
      ["Ticket ref", ticketRef || "—"],
      ["Reason", delegation.reason || "—"],
    ],
    styles: { fontSize: 9 },
    headStyles: { fillColor: [61, 142, 240], textColor: [255, 255, 255] },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 60 } },
  });

  doc.save(`${filename("Delegation")}.pdf`);
}

export function exportTracePDF({ hops, criteria, operator, ticketRef }) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Message Trace", operator, ticketRef);

  doc.setFontSize(8);
  Object.entries(criteria).filter(([, v]) => v).forEach(([k, v]) => {
    doc.text(`${k}: ${v}`, 14, y);
    y += 5;
  });
  y += 4;

  autoTable(doc, {
    startY: y,
    head: [["#", "Event", "Detail", "Timestamp"]],
    body: hops.map((h, i) => [i + 1, h.event, h.detail, h.time]),
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [139, 126, 248], textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [248, 248, 250] },
  });

  doc.save(`${filename("Trace")}.pdf`);
}

export function exportAuditLogPDF(entries) {
  const doc = new jsPDF("l"); // landscape
  addHeader(doc, "Audit Log", "All operators", null);

  autoTable(doc, {
    startY: 50,
    head: [["Timestamp", "Operator", "Module", "Action", "Ticket Ref", "Matched", "Deleted", "Failed"]],
    body: entries.map((e) => [
      e.timestamp,
      e.operatorEmail,
      e.module,
      e.action,
      e.ticketRef || "—",
      e.matchCount ?? "—",
      e.successCount ?? "—",
      e.failCount ?? "—",
    ]),
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: IMA_ORANGE, textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [248, 248, 250] },
  });

  doc.save(`${filename("AuditLog")}.pdf`);
}

// ─── CSV ──────────────────────────────────────────────────────────────────────

function downloadCSV(data, name) {
  const csv = Papa.unparse(data);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function exportPurgeCSV({ results, criteria, operator, ticketRef }) {
  downloadCSV(
    results.map((r) => ({
      timestamp: new Date().toISOString(),
      operator,
      ticket_ref: ticketRef || "",
      mailbox: r.userEmail,
      subject: r.subject,
      sender: r.sender,
      received: r.received,
      result: r.status,
      error: r.error || "",
      ...criteria,
    })),
    filename("Purge")
  );
}

export function exportExfilCSV(alerts) {
  downloadCSV(
    alerts.map((a) => ({
      timestamp: a.time,
      user: a.user,
      severity: a.severity,
      type: a.type,
      event: a.event,
      recipients: (a.recipients || []).join("; "),
    })),
    filename("ExfilAudit")
  );
}

export function exportTraceCSV(hops, criteria) {
  downloadCSV(
    hops.map((h, i) => ({ hop: i + 1, event: h.event, detail: h.detail, time: h.time, ...criteria })),
    filename("MessageTrace")
  );
}

export function exportAuditLogCSV(entries) {
  downloadCSV(entries, filename("AuditLog"));
}
